import { useApiClient, useErrorHandler, useToastService } from "@@common";
import { AuthContext } from "@@components";
import { useQueryClient } from "@tanstack/react-query";
import React, { PropsWithChildren, useContext, useEffect, useRef, useState } from "react";
import { OperationClient } from "../../api/api";
import Spinner from "../../common/components/Spinner";
import BundleContext from "../../UserInterfaceModule/Contexts/BundleContext";
import { NavigationService } from "../../UserInterfaceModule/Services/NavigationService";
import { NotifyService } from "../../UserInterfaceModule/Services/NotifyService";
import { IExpressionEvaluatorContext } from "../Models/IExpressionEvaluatorContext";
import { BundleServicesKey } from "../Runtime/Evaluators/DomainServices/DomainServiceEnumItemsExpressionEvaluator";
import { ApiServices, ApiServicesKey } from "../Runtime/Evaluators/DomainServices/DomainServiceOperationCallExpressionEvaluator";
import { ExpressionEvaluatorContext as ExpressionEvaluatorContextClass } from "../Runtime/ExpressionEvaluatorContext";
import { ObjectVariableScope } from "../Runtime/VariableScopes/ObjectVariableScope";
import { VariableScopeObserver } from "../Runtime/VariableScopes/VariableScopeObserver";
import ExpressionEvaluatorContext from "./ExpressionEvaluatorContext";

interface ExpressionEvaluatorContextProviderProps extends PropsWithChildren {
  logChanges?: boolean;
}

export const ExpressionEvaluatorContextProvider: React.FC<ExpressionEvaluatorContextProviderProps> = ({ children, logChanges: logChanges = false }) => {
  const [context, setContext] = useState<IExpressionEvaluatorContext>();
  const [isPending, setIsPending] = useState(true);
  const hasInitializedRef = useRef(false);

  const auth = useContext(AuthContext);

  const errorHandler = useErrorHandler();
  const operationClient = useApiClient(OperationClient);
  const queryClient = useQueryClient();
  const bundle = useContext(BundleContext);
  const toastService = useToastService();

  useEffect(() => {
    // Prevent double-initialization in React StrictMode (development only)
    if (hasInitializedRef.current) {
      return;
    }
    hasInitializedRef.current = true;

    async function initializeContext() {
      setIsPending(true);
      try {
        const variableScope = new ObjectVariableScope();

        const variableScopeObserver: VariableScopeObserver = (target, name, value) => {
          console.log(`Variable changed '${name}' changed`, value, variableScope);
        };

        const expressionContext = new ExpressionEvaluatorContextClass(variableScope);

        expressionContext.SetProperty<ApiServices>(ApiServicesKey, { operationClient, queryClient, errorHandler });
        expressionContext.SetProperty(BundleServicesKey, { bundle });
        expressionContext.SetProperty(NavigationService.KEY, new NavigationService());
        expressionContext.SetProperty(NotifyService.KEY, new NotifyService());

        const ctxVariableScope = new ObjectVariableScope();

        const sessionVariableScope = new ObjectVariableScope();
        const userVariableScope = new ObjectVariableScope();
        userVariableScope.set("userId", auth?.account?.localAccountId ?? null);

        if (logChanges) {
          ctxVariableScope.addObserver(variableScopeObserver);
          userVariableScope.addObserver(variableScopeObserver);
          sessionVariableScope.addObserver(variableScopeObserver);
        }

        expressionContext.VariableScope.set("$ctx", ctxVariableScope);
        expressionContext.VariableScope.set("$session", sessionVariableScope);
        expressionContext.VariableScope.set("$user", userVariableScope);

        if (logChanges) {
          variableScope.addObserver(variableScopeObserver);
        }

        setContext(expressionContext);
      } catch (error) {
        await errorHandler(error);
      } finally {
        setIsPending(false);
      }
    }

    initializeContext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.account?.localAccountId]); // Initialize once on mount

  useEffect(() => {
    if (!context) return;
    context.GetProperty<NotifyService>(NotifyService.KEY)?.setToastService(toastService);
  }, [context, toastService]);

  if (isPending || context === undefined) {
    return <Spinner />;
  }

  return <ExpressionEvaluatorContext.Provider value={context}>{children}</ExpressionEvaluatorContext.Provider>;
};

export default ExpressionEvaluatorContextProvider;
